import { Collection, Root, Subsurvey, DefinitionComponent, BlockReference, ObjectReference, TemplateIterationReference, TemplatedBlock, TemplateIterationsReference, TopLevelTemplatedBlock, GenericTemplatedBlock, TemplateSubstitutionReference, TemplateSubstitutionsReference, TemplateSubstitution, TemplateSubstitutions, TemplateIteration, TemplateIterations } from "../survey";
import { referenceBlocksArray, replaceReferenceWithDefinition } from "./references";
import { getComponentIdentifier, WAYPOINT_DELIMITER } from "./componentIdentifier";
import { expandPaymentPreferences } from "./paymentPreferences";

export const ITERATION_CHILD_ID_SEPARATOR = '_';

// Literal replace via split/join (no RegExp, so regex-special chars in `key` are safe).
// The `includes` guard skips the string copy when there's nothing to replace.
function replaceAllLiteral(haystack: string, key: string, value: string): string {
  if (!haystack.includes(key)) return haystack;
  return haystack.split(key).join(value);
}

// Keys whose values are identifiers, not user-facing text — never substituted.
// `_targetFieldPreSubstitution` preserves the un-substituted target field (e.g.
// `payment_1000_<<num>>`) for payment-computation grouping. `_preSubstitutionIdentifier` preserves
// the component's getComponentIdentifier value before substitution, so an expanded component (e.g. a
// Notifications/Payments tab row) can be traced back to the single un-expanded component the editor
// renders — its scroll-to path. Both survive substitution verbatim by being listed here.
const NON_REPLACED_KEYS = new Set(['kind', 'id', '_id', '_targetFieldPreSubstitution', '_preSubstitutionIdentifier']);

function jsonReplace(obj: any, substitutions: {key: string, value: string}[], idPath?: string): any {
  if (Array.isArray(obj)) {
    const out = new Array(obj.length);
    for (let i = 0; i < obj.length; i++) {
      out[i] = jsonReplace(obj[i], substitutions, idPath);
    }
    return out;
  } else if (typeof obj === 'object' && obj !== null) {
    const out: any = {};
    for (const k of Object.keys(obj)) {
      // Do not replace non-user-input values
      if (NON_REPLACED_KEYS.has(k)) {
        out[k] = (idPath && k === 'id')
          ? `${idPath}${ITERATION_CHILD_ID_SEPARATOR}${obj[k]}`
          : obj[k];
        continue;
      }
      out[k] = jsonReplace(obj[k], substitutions, idPath);
    }
    return out;
  } else if (typeof obj === 'string'){
    if (substitutions.length) {
      let toReturn = obj;
      for (const {key, value} of substitutions) {
        toReturn = replaceAllLiteral(toReturn, key, value);
      }
      return toReturn;
    }
  }
  return obj;
}

/**
 * Detects reference objects (SubsurveyRef, DashboardRef, ApplicantIdentitiesRef, PaymentsPage)
 * that point to other entities by path.
 */
function isReferenceObject(obj: any): boolean {
  if (!obj || typeof obj !== 'object' || !obj.kind) return false;
  switch (obj.kind) {
    case 'Subsurvey':
      return !obj.sections;
    case 'Dashboard':
      return !obj.components;
    case 'ApplicantIdentities':
    case 'Payments Page':
      return true;
    default:
      return false;
  }
}

/**
 * Suffixes id on each top-level expanded item and recurses into nested children.
 * Only called when tagTemplatedBlocks is true (admin-panel publish path).
 * iterationSuffix is the first substitution value for this iteration (stable id suffix).
 */
function tagAndSuffixExpandedItems(toInsert: any[], originals: any[], iterationSuffix: string, hasIterationId: boolean): void {
  for (let i = 0; i < toInsert.length; i++) {
    const item = toInsert[i];
    const original = originals[i];
    if (!item || typeof item !== 'object') continue;

    // id suffixing — only when the iteration has no explicit id. When it does, jsonReplace has
    // already prefixed every id with `${iter_id}.` to disambiguate this iteration, so suffixing
    // would double-decorate the id (and break the prefix-based raw-id lookup in dashboard_service).
    if (!hasIterationId && item.id && original?.id && item.id === original.id) {
      item.id = `${item.id}_${iterationSuffix}`;
    }

    // top-level tagging + recurse into nested children
    item._fromTemplatedBlock = true;
    recursivelyMarkTemplatedDescendants(item, iterationSuffix, true, /* skipSelf */ true, hasIterationId);
  }
}

function recursivelyMarkTemplatedDescendants(obj: any, iterationSuffix: string, tagTemplatedBlocks?: boolean, skipSelf?: boolean, hasIterationId?: boolean): void {
  if (!obj || typeof obj !== 'object') return;
  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      recursivelyMarkTemplatedDescendants(obj[i], iterationSuffix, tagTemplatedBlocks, /* skipSelf */ false, hasIterationId);
    }
    return;
  }
  // Skip reference objects whose path is a pointer to another entity, not a component identifier
  if (isReferenceObject(obj)) return;

  if (!skipSelf) {
    // Only mark objects that represent survey components (have a 'kind')
    if (tagTemplatedBlocks && obj.kind) {
      obj._fromTemplatedBlock = true;
    }
    // Suffix id (iteration disambiguator only — see tagAndSuffixExpandedItems). Skipped when the
    // iteration has an explicit id, since jsonReplace already prefixed the id with `${iter.id}.`.
    if (!hasIterationId && obj.id && typeof obj.id === 'string') {
      obj.id = obj.id + '_' + iterationSuffix;
    }
  }

  // Recurse into all object properties
  for (const key of Object.keys(obj)) {
    if (key === '_fromTemplatedBlock') continue;
    if (obj[key] && typeof obj[key] === 'object') {
      recursivelyMarkTemplatedDescendants(obj[key], iterationSuffix, tagTemplatedBlocks, /* skipSelf */ false, hasIterationId);
    }
  }
}

/**
 * Resolves an array of items where each item is either an inline value or an ObjectReference.
 * Inline values pass through unchanged. References are looked up in definitionsById and replaced
 * with their definition. References with missing or empty definitionIds are dropped.
 */
function resolveReferencesInArray<T>(
  items: ReadonlyArray<T | ObjectReference>,
  definitionsById: Map<string, DefinitionComponent> | undefined,
): T[] {
  // TODO: We may want to log/capture missing-definition cases here and below (for substitutions) for debugging purposes
  const out: T[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object' || !('definitionId' in item)) {
      if (item) out.push(item as T);
      continue;
    }
    if (!item.definitionId) continue;
    const def = definitionsById?.get(item.definitionId);
    if (!def) continue;
    const replaced = replaceReferenceWithDefinition(item, def) as T | null;
    if (replaced) out.push(replaced);
  }
  return out;
}

/**
 * Resolves a Templated Block's `iterations` into a flat list of TemplateIterations.
 * Each entry is one of:
 *   - an inline iteration ({ substitutions }) — used directly
 *   - a Template Iteration Reference — resolved to a single iteration
 *   - a Template Iterations Reference — resolved to a container, then flattened into its iterations
 *     (whose inner entries may themselves be single references)
 * References whose definitions are missing are dropped.
 */
function resolveIterations(
  items: TemplatedBlock['iterations'],
  definitionsById: Map<string, DefinitionComponent> | undefined,
): TemplateIteration[] {
  // Give inline iterations an explicit kind so every entry below is a tagged TemplateIteration.
  const tagged = items.map((i) =>
    'kind' in i ? i : { kind: 'Template Iteration' as const, ...i },
  );

  const out: TemplateIteration[] = [];
  for (const resolved of resolveReferencesInArray<TemplateIteration | TemplateIterations>(tagged, definitionsById)) {
    if (resolved.kind === 'Template Iterations') {
      out.push(...resolveReferencesInArray<TemplateIteration>(resolved.iterations, definitionsById));
    } else {
      out.push(resolved);
    }
  }
  return out;
}

/**
 * Resolves a Template Iteration's `substitutions` into a flat list of { key, value } pairs.
 * Each entry is one of:
 *   - an inline { key, value } — used directly
 *   - a Template Substitution Reference — resolved to a single pair
 *   - a Template Substitutions Reference — resolved to a container, then flattened into its pairs
 *     (whose inner entries may themselves be single references)
 * References whose definitions are missing are dropped.
 */
function resolveSubstitutions(
  items: TemplateIteration['substitutions'],
  definitionsById: Map<string, DefinitionComponent> | undefined,
): { key: string, value: string }[] {
  const toKeyValue = (s: TemplateSubstitution | { key: string, value: string }) =>
    'kind' in s ? { key: s.key, value: s.value } : s;

  const out: { key: string, value: string }[] = [];
  for (const resolved of resolveReferencesInArray<TemplateSubstitution | { key: string, value: string } | TemplateSubstitutions>(items, definitionsById)) {
    if ('kind' in resolved && resolved.kind === 'Template Substitutions') {
      for (const inner of resolveReferencesInArray<TemplateSubstitution | { key: string, value: string }>(resolved.substitutions, definitionsById)) {
        out.push(toKeyValue(inner));
      }
    } else {
      out.push(toKeyValue(resolved));
    }
  }
  return out;
}

/**
 * Resolves a Templated Block's `iterations` into the final, fully-substituted result: an array of
 * iterations, each an array of { key, value } substitution pairs with all Template Iteration(s) and
 * Template Substitution(s) references expanded against `definitionsById`.
 *
 * This is the same resolution `expandGenericTemplates` performs before applying the substitutions to
 * the block's components, exposed on its own so callers (e.g. the config editor's preview) can show
 * what a Templated Block will ultimately expand to without rendering the components.
 */
export function resolveTemplatedBlockIterations(
  iterations: TemplatedBlock['iterations'],
  definitionsById?: Map<string, DefinitionComponent>,
): { key: string, value: string }[][] {
  return resolveIterations(iterations, definitionsById)
    .map((iter) => resolveSubstitutions(iter.substitutions ?? [], definitionsById));
}

/**
 * Structural-sharing string substitution. Identical in result to `jsonReplace` (same key handling,
 * same NON_REPLACED_KEYS exemptions), but returns the SAME reference for any string, array, or
 * object whose subtree contains no substitution — allocating only along the paths that actually
 * change. A large, mostly-static component therefore costs almost nothing to substitute, and the
 * result shares its unchanged subtrees with the input instead of deep-copying them.
 *
 * This is safe ONLY for read-only consumers: because the output aliases the input's untouched
 * subtrees, a caller that mutates the result would corrupt the input. It is used exclusively by
 * `applyTemplateSubstitutions` (config search, which never mutates the substituted view). The
 * expansion pipeline keeps using `jsonReplace`, which always deep-copies and whose output IS mutated
 * downstream (id suffixing, `_fromTemplatedBlock` tagging).
 */
function substituteShared(node: any, substitutions: { key: string, value: string }[]): any {
  if (typeof node === 'string') {
    let out = node;
    for (const { key, value } of substitutions) {
      out = replaceAllLiteral(out, key, value);
    }
    return out; // replaceAllLiteral returns the same string when the key is absent, so identity is preserved when unchanged
  }
  if (Array.isArray(node)) {
    let out: any[] | null = null; // allocated lazily on the first changed element
    for (let i = 0; i < node.length; i++) {
      const replaced = substituteShared(node[i], substitutions);
      if (out === null && replaced !== node[i]) out = node.slice(0, i);
      if (out !== null) out.push(replaced);
    }
    return out === null ? node : out;
  }
  if (node !== null && typeof node === 'object') {
    let out: any = null; // allocated lazily on the first changed property
    const keys = Object.keys(node);
    for (let ki = 0; ki < keys.length; ki++) {
      const k = keys[ki];
      const replaced = NON_REPLACED_KEYS.has(k) ? node[k] : substituteShared(node[k], substitutions);
      if (out === null && replaced !== node[k]) {
        out = {};
        for (let kj = 0; kj < ki; kj++) out[keys[kj]] = node[keys[kj]];
      }
      if (out !== null) out[k] = replaced;
    }
    return out === null ? node : out;
  }
  return node;
}

/**
 * Applies one iteration's resolved substitutions to a component (or component subtree). The longest
 * keys are substituted first so that a key that is a prefix of another (e.g. `<<n>>` vs `<<num>>`)
 * doesn't partially clobber it.
 *
 * Exposed for callers that need to evaluate what a single template component looks like for a given
 * iteration WITHOUT multiplying the block into the full expanded survey (e.g. config search, which
 * walks the un-expanded survey but must match/display substituted values). Unlike full expansion this
 * does not resolve references, tag `_fromTemplatedBlock`, or suffix ids.
 *
 * Uses structural sharing (see `substituteShared`): the result aliases the input's unchanged
 * subtrees, so the caller MUST treat it as read-only.
 */
export function applyTemplateSubstitutions<T>(node: T, substitutions: { key: string, value: string }[]): T {
  if (!substitutions.length) return node;
  const sorted = [...substitutions].sort((a, b) => b.key.length - a.key.length);
  return substituteShared(node, sorted);
}

const KINDS_TO_TAG_WITH_PARENT_TEMPLATE_ID = [
  'Subsurvey',
  'Payment',
  // Notifications/Collection/Section/Dashboard/Applicant Identities/Applicant Portal Page are tagged so
  // the Notifications tab can point each row's scroll-to at the single Templated Block the editor renders
  'Notification',
  'InlineNotification',
  'Collection',
  'Section',
  'Dashboard',
  'Applicant Identities',
  'Applicant Portal Page',
];

export function expandGenericTemplates(
  obj: any,
  definitionsById?: Map<string, DefinitionComponent>,
  options?: {
    tagTemplatedBlocks?: boolean,
    parentTemplatedBlockId?: string,
  }
): any {
  if (Array.isArray(obj)) {
    const toReturn = obj.map((o: any) => expandGenericTemplates(o, definitionsById, options));
    return toReturn.reduce((acc, o) => {
      // Prevents explosions from nullish values
      if (!o) return acc;

      if (o.kind === 'Templated Block') {
        // Variable so we can specify it's type
        const template = o as TemplatedBlock | GenericTemplatedBlock<any> | TopLevelTemplatedBlock;

        // Collect all top-level iterations, expanding any references — including Template Iterations
        // containers, which flatten into multiple iterations.
        const iterations = resolveIterations(template.iterations, definitionsById);

        const usedSuffixes = new Set<string>();
        // Now for each iteration, collect all substitutions, replacing references with definitions along the way
        for (let iterIdx = 0; iterIdx < iterations.length; iterIdx++) {
          const iter = iterations[iterIdx];
          const replacedSubs = resolveSubstitutions(iter.substitutions ?? [], definitionsById);

          const candidateSuffix = replacedSubs[0]?.value ?? replacedSubs.map((s: any) => s.value).join('_');
          const iterationSuffix = usedSuffixes.has(candidateSuffix) ? String(iterIdx) : candidateSuffix;
          usedSuffixes.add(iterationSuffix);
          const sortedSubs = replacedSubs.sort((a: any, b: any) => b.key.length - a.key.length);
          const toInsert = jsonReplace(
            template.components,
            sortedSubs,
            iter.id
          );
          if (options?.tagTemplatedBlocks) {
            tagAndSuffixExpandedItems(toInsert, o.components, iterationSuffix, Boolean(iter.id));
          }
          acc.push(...toInsert);
        }

      } else if (o.kind === 'Payment Preferences' && !o._expanded) {
        acc.push(...expandPaymentPreferences(o));

      // Right now, Subsurvey is treated as a special case since we KNOW there is a possible reference property (review).
      // In the future, we may want to make this more general and traverse all object properties for references.
      // But for now, we handle this case specially in the interest of speed.
      } else if (o.kind === 'Subsurvey') {
        if (o.review && o.review.kind === 'ReviewConfiguration Reference') {
          const definition = definitionsById?.get(o.review.definitionId);
          if (definition) {
            acc.push({ ...o, review: replaceReferenceWithDefinition(o.review, definition) });
            return acc;
          }
        }

        // Push the whole subsurvey with the replaced review configuration
        acc.push(o);
      } else if (referenceBlocksArray.includes(o.kind)) {
        const definition = definitionsById?.get((o as BlockReference).definitionId);
        if (definition) {
          const replaced = replaceReferenceWithDefinition(o, definition);
          if (replaced) {
            acc.push(replaced);
          }
        } else {
          // Push the reference, perhaps we'll find it later.
          acc.push(o);
        }
      } else {
        acc.push(o);
      }
      return acc;
    }, []);
  } else if (typeof obj === 'object' && obj !== null) {
    // Update the options to ensure that all children of Templated Blocks have the parentTemplatedBlockId set
    const childOptions = (obj.kind === 'Templated Block' && obj.id)
      ? { ...options, parentTemplatedBlockId: obj.id }
      : options;
    // Build the copy first, then tag the copy below — `obj` may be someone else's object
    // (e.g. a memoized/frozen TrackedObject.read() result), so we must never write to it.
    const result = Object.keys(obj).reduce((o: any, k: string) => {
      o[k] = expandGenericTemplates(obj[k], definitionsById, childOptions);
      return o;
    }, {});
    if (options?.parentTemplatedBlockId && KINDS_TO_TAG_WITH_PARENT_TEMPLATE_ID.includes(obj.kind)) {
      result._parentTemplatedBlockId = options.parentTemplatedBlockId;
      // These run on the template component, ahead of jsonReplace, so they capture the
      // pre-substitution (placeholder) values. `_targetFieldPreSubstitution` groups expanded payments
      // by their originating distro payment kind
      if (typeof obj.targetField === 'string') {
        result._targetFieldPreSubstitution = obj.targetField;
      }
      // `_preSubstitutionIdentifier` is the component's kind-qualified identifier before substitution
      // this allows tying an expanded component back to its unexpanded equivalent so scroll-to can
      // descend to it. Kind is included to avoid parent/child name collisions.
      const preSubstitutionIdentifier = getComponentIdentifier(obj);
      if (preSubstitutionIdentifier) {
        result._preSubstitutionIdentifier = obj.kind + WAYPOINT_DELIMITER + preSubstitutionIdentifier;
      }
    }
    return result;
  }
  return obj;
}

export function addGeneratedSubsurveys<T extends Root>(obj: T): T {
  function processCollection(collection: Collection): Collection {
    // Permissions are based on Collections, so we need to append subsurveys to their nearest parent Collection
    const localSubsurveysToAppend: Subsurvey[] = [];
    let newComponents = collection.components.flatMap((component: any) => {
      return processObjectOrArray(component, localSubsurveysToAppend);
    });

    // Filter subsurveys that don't already exist
    const subsurveysToAdd = localSubsurveysToAppend.filter(subsurvey =>
      !newComponents.some((component: any) => component.kind === 'Subsurvey' && component.path === subsurvey.path)
    );

    return { ...collection, components: [...newComponents, ...subsurveysToAdd] };
  }
  function processObjectOrArray(item: any, subsurveysToAppend: Subsurvey[]): any {
    // Create a subsurvey for each attachment that has allowSendingLinkToUpload set to true
    // these can be shared with applicants via linked subsurveys.
    if (item.kind === 'Attachment' && item.allowSendingLinkToUpload === true) {
      const subsurvey: Subsurvey = {
        kind: 'Subsurvey',
        path: item.targetField + '_automated_collection_form',
        defaultExpiration: {days: 1, weeks: 0, months: 0},
        hideFromNavigation: true,
        sections: [{
          kind: 'Section',
          // TODO: we likely don't want to import all of /i18n for this single use-case,
          // but it would be good to not have to manually maintain these transltions.
          title: {
            'am': 'ሰነድ ስቀል',
            'ar': 'تحميل المستند',
            'bn': 'নথি আপলোড করুন',
            'cni': 'Subir documento',
            'en': 'Upload Document',
            'es': 'Subir documento',
            'fa_AF': 'بارگذاری سند',
            'fa': 'بارگذاری سند',
            'fr': 'Télécharger le document',
            'ha': 'Aika Takardar',
            'hmn': 'Upload ntaub ntawv',
            'ht': 'Telechaje Dokiman',
            'ko': '문서 업로드',
            'mh': 'Upload peba eo',
            'ne': 'डोक्युमेन्ट अपलोड गर्नुहोस्',
            'om': 'Upload Document',
            'pl': 'Prześlij dokument',
            'ps_AF': 'د اسنادو پورته کول',
            'pt': 'Carregar Documento',
            'pua': 'Juchukua Documento',
            'ru': 'Загрузить документ',
            'so': 'Soo rar dukumeenti',
            'tl': 'Mag-upload ng Dokumento',
            'ua': 'Завантажити документ',
            'vi': 'Tải lên tài liệu',
            'yue': '上传文件',
            'zh_CN': '上传文档',
          },
          components: [{
            ...item, // duplicate the attachment component
            allowSendingLinkToUpload: false,
          }]
        }]
      };
      subsurveysToAppend.push(subsurvey);
      return { ...item };
    } else if (item.kind === 'Collection') {
      return processCollection(item);
    } else if (Array.isArray(item)) {
      return item.map(i => processObjectOrArray(i, subsurveysToAppend));
    } else if (item.survey) {
      item.survey = processObjectOrArray(item.survey, subsurveysToAppend);
    } else if (['Section', 'Conditional Block'].includes(item.kind)) {
      item.components = processObjectOrArray(item.components, subsurveysToAppend);
    } else if (['Subsurvey'].includes(item.kind)) {
      item.sections = processObjectOrArray(item.sections, subsurveysToAppend);
    }
    return item;
  }

  const subsurveysToAppend: Subsurvey[] = [];
  const expanded = processObjectOrArray(obj, subsurveysToAppend);

  if (subsurveysToAppend.length) {
    // append the subsurveys to the root of the expanded survey if they aren't in a Collection
    if (Array.isArray(expanded)) {
      return [
        ...expanded,
        ...subsurveysToAppend.filter(subsurvey => !expanded.some((component: any) => component.kind === 'Subsurvey' && component.path === subsurvey.path))
      ] as T;
    } else {
      expanded.survey = [
        ...expanded.survey,
        ...subsurveysToAppend.filter(subsurvey => !expanded.survey.some((component: any) => component.kind === 'Subsurvey' && component.path === subsurvey.path))];
      return expanded;
    }
  }

  return expanded;
}
